import type { RefObject } from 'react';
import { memo } from 'react';

import { cssClasses } from 'pkg/css/utils';
import DateTime from 'pkg/datetime';

import MaterialSymbol from 'components/material-symbols';

import {
	RANGE_OPTION,
	type CalendarStateInterface,
	type CalendarNavigationHandlers,
} from 'components/form/calendar/types';
import * as Input from 'components/form/inputs';
import Column from 'components/layout/column';
import Row from 'components/layout/row';

import Button from 'design/button';

import * as css from './styles.css';

interface CalendarNavigationProps {
	range: boolean;
	calendarState: CalendarStateInterface;
	fromDateRef: RefObject<HTMLInputElement>;
	toDateRef: RefObject<HTMLInputElement>;
	handlers: CalendarNavigationHandlers;
}

export const CalendarNavigation = memo(
	({
		range,
		calendarState,
		fromDateRef,
		toDateRef,
		handlers,
	}: CalendarNavigationProps) => {
		const calendarDate = new DateTime(calendarState.renderedDate);

		return (
			<Column>
				{range && (
					<Row>
						<Input.Field
							data-rangeoption={RANGE_OPTION.from}
							defaultValue={calendarState.fromDateRefVal}
							onFocus={handlers.onInputFocus}
							onBlur={handlers.onInputBlur}
							ref={fromDateRef}
							active={calendarState.currentRangeOption === RANGE_OPTION.from}
						/>
						<Input.Field
							defaultValue={calendarState.toDateRefVal}
							data-rangeoption={RANGE_OPTION.to}
							onFocus={handlers.onInputFocus}
							onBlur={handlers.onInputBlur}
							ref={toDateRef}
							active={calendarState.currentRangeOption === RANGE_OPTION.to}
						/>
					</Row>
				)}
				<div className={css.header}>
					<Button
						className={css.navButton}
						small
						transparent
						icon="chevron_left"
						onClick={handlers.onNavigatePrev}
					/>

					{range ? (
						<time className={css.navigationLocation}>
							{calendarDate.toLocaleDateString({
								month: 'short',
								year: 'numeric',
							})}
						</time>
					) : (
						<time className={css.navigationLocation}>
							{calendarDate.toLocaleDateString({
								month: 'short',
							})}
							<span
								className={cssClasses(
									css.yearButton,
									calendarState.showYearPicker ? css.open : ''
								)}
								onClick={handlers.onToggleYearPicker}>
								{calendarDate.toLocaleDateString({
									year: 'numeric',
								})}
								<MaterialSymbol variant="keyboard_arrow_down" />
							</span>
						</time>
					)}

					<Button
						className={css.navButton}
						small
						transparent
						icon="chevron_right"
						onClick={handlers.onNavigateNext}
						testid="calendar.navigate.next"
					/>
				</div>
			</Column>
		);
	}
);
